import { useEffect, useRef } from 'react';
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  Timestamp,
  limit,
} from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { db } from '../../lib/firebase/config';
import {
  SoundboardSound,
  SoundboardTrigger,
  SoundboardStopTrigger,
  subscribeToSounds,
  subscribeToTrigger,
  subscribeToStopTrigger,
  subscribeToEventSounds,
  SoundboardEventSounds,
  EventSoundConfig,
  resolveEventSoundIds,
  getStoredVolume,
  getStoredMuted,
  getStoredDnd,
  registerActiveAudio,
  stopAllSounds,
} from '../../services/soundboardService';
import { playLiveChatChime } from '../../services/liveChatNotificationService';

/**
 * Mounted in the app shell for every soundboard-enabled user. Two responsibilities:
 *
 * 1. **Manual broadcast playback** — listens to `soundboardState/current` and
 *    plays the matching library sound whenever a new trigger arrives (i.e.
 *    when any other soundboard user clicks a button).
 *
 * 2. **Event-sound playback** — for each configured event mapping (e.g.
 *    `newSale`), watches the source collection for fresh documents and plays
 *    the assigned sound locally. Fires once per new doc, only for documents
 *    created *after* the listener mounted (so reloading the app doesn't
 *    replay yesterday's sales).
 *
 * Synchronization: both paths schedule playback at a future "play-at" target
 * derived from a *server* timestamp (Firestore `serverTimestamp` for manual
 * triggers, `createdAt` for sales). Each client's `setTimeout` fires relative
 * to its own `Date.now()`, so as long as client clocks are roughly in sync
 * with each other (typical NTP drift is well under 100ms), every recipient
 * starts playing within an audibly-coincident window.
 */
// Lead time added on top of the server-stamped reference time. Big enough to
// absorb Firestore propagation jitter and a touch of audio prebuffer; small
// enough that the click-to-hear feel remains snappy.
const PLAY_LEAD_MS = 600;

// Window after a conversation is created during which its customer message is
// still considered the "initial" contact. Covers the gap between the
// conversation doc being created and its first message write landing. Anything
// later is treated as a follow-up (and gets the subtle chime instead).
const CHAT_INITIAL_WINDOW_MS = 20_000;
export const SoundboardListener: React.FC = () => {
  const { currentUser } = useAuth();

  const isMasterAdmin = !!currentUser?.roles?.includes(UserRole.MasterAdmin);
  const hasAccess = !!currentUser?.soundboardAccess || isMasterAdmin;

  const soundsRef = useRef<Map<string, SoundboardSound>>(new Map());
  const eventSoundsRef = useRef<SoundboardEventSounds>({});
  const lastTriggerKey = useRef<string>('');
  const lastStopKey = useRef<string>('');
  const isFirstSnapshot = useRef<boolean>(true);
  const isFirstStopSnapshot = useRef<boolean>(true);

  useEffect(() => {
    if (!hasAccess) return;

    const myUid = currentUser?.uid ?? null;
    isFirstSnapshot.current = true;
    isFirstStopSnapshot.current = true;
    lastTriggerKey.current = '';
    lastStopKey.current = '';

    // ── Sound library snapshot ───────────────────────────────────────────
    const unsubSounds = subscribeToSounds(
      (list) => {
        soundsRef.current = new Map(list.map((s) => [s.id, s]));
      },
      () => {
        soundsRef.current = new Map();
      },
    );

    // ── Manual broadcast trigger ────────────────────────────────────────
    const unsubTrigger = subscribeToTrigger((trigger: SoundboardTrigger) => {
      // Pending writes from this very client surface a snapshot before the
      // server has stamped triggeredAt. Defer until the server-confirmed
      // snapshot — that's the one every client sees with the same value, so
      // it's the only one we can sync to.
      const triggeredAtMs = (trigger.triggeredAt as { toMillis?: () => number } | null)?.toMillis?.();
      if (!triggeredAtMs) return;

      // Dedup on the unique nonce per click. Including the timestamp would
      // double-fire on the sender (pending write -> server confirm).
      const key = trigger.nonce ?? `${trigger.soundId}:${triggeredAtMs}`;

      // Skip the very first snapshot — that's whatever was last written to
      // the doc before this client connected, not a fresh broadcast.
      if (isFirstSnapshot.current) {
        isFirstSnapshot.current = false;
        lastTriggerKey.current = key;
        return;
      }

      if (key === lastTriggerKey.current) return;
      lastTriggerKey.current = key;

      // Debug: every soundboard listener logs each trigger so we can verify
      // what was sent, what we are, and whether the targeting filter
      // engaged. Safe to remove once the new feature has settled in.
      const willPlay = !trigger.targetUserId || trigger.targetUserId === myUid;
      console.log(
        `[Soundboard] received sound="${trigger.soundName}" targetUserId=${trigger.targetUserId ?? 'null'} myUid=${myUid ?? 'null'} willPlay=${willPlay} from=${trigger.triggeredByName ?? trigger.triggeredBy}`,
      );

      // Targeted trigger — only the recipient listens.
      if (trigger.targetUserId && trigger.targetUserId !== myUid) {
        return;
      }

      const sound = soundsRef.current.get(trigger.soundId);
      if (!sound) return;

      schedulePlay(sound, triggeredAtMs);
    });

    // ── Stop broadcast ──────────────────────────────────────────────────
    // When any user clicks Stop in All/User mode, every recipient (or just
    // the targeted user) immediately pauses any audio currently playing.
    const unsubStop = subscribeToStopTrigger((stop: SoundboardStopTrigger) => {
      const stoppedAtMs = (stop.stoppedAt as { toMillis?: () => number } | null)?.toMillis?.();
      if (!stoppedAtMs) return;

      const key = stop.nonce ?? `${stoppedAtMs}`;
      if (isFirstStopSnapshot.current) {
        isFirstStopSnapshot.current = false;
        lastStopKey.current = key;
        return;
      }
      if (key === lastStopKey.current) return;
      lastStopKey.current = key;

      // Targeted stop — only that user pauses.
      if (stop.targetUserId && stop.targetUserId !== myUid) return;

      stopAllSounds();
    });

    // ── Event-sound mapping ─────────────────────────────────────────────
    const unsubEventMap = subscribeToEventSounds(
      (s) => {
        eventSoundsRef.current = s;
      },
      () => {
        eventSoundsRef.current = {};
      },
    );

    // Pick a sound for an event config. When the config has multiple sounds,
    // one is chosen at random each time (ignoring any ids that aren't loaded).
    const pickEventSound = (cfg?: EventSoundConfig): SoundboardSound | undefined => {
      const ids = resolveEventSoundIds(cfg).filter((id) => soundsRef.current.has(id));
      if (ids.length === 0) return undefined;
      const id = ids[Math.floor(Math.random() * ids.length)];
      return soundsRef.current.get(id);
    };

    // ── Sales listener (event: newSale) ─────────────────────────────────
    // We listen for any sale doc with `createdAt` strictly later than this
    // client's mount time. We also discard any doc older than 30s on the
    // wire so reconnects after a long offline stretch don't blast 50
    // sounds at once.
    const mountTs = Timestamp.fromDate(new Date());
    const seenSaleIds = new Set<string>();
    const salesQuery = query(
      collection(db, 'sales'),
      where('createdAt', '>', mountTs),
      orderBy('createdAt', 'desc'),
      limit(20),
    );

    const unsubSales = onSnapshot(
      salesQuery,
      (snap) => {
        const cfg = eventSoundsRef.current.newSale;
        if (!cfg?.enabled || resolveEventSoundIds(cfg).length === 0) return;

        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const id = change.doc.id;
          if (seenSaleIds.has(id)) return;
          seenSaleIds.add(id);

          const data = change.doc.data() as { createdAt?: Timestamp };
          const created = data.createdAt;
          const createdMs = created?.toMillis?.() ?? Date.now();
          // Skip ancient docs (e.g. from a delayed catch-up).
          if (created) {
            const ageMs = Date.now() - createdMs;
            if (ageMs > 30_000) return;
          }

          const sound = pickEventSound(cfg);
          if (!sound) return;
          schedulePlay(sound, createdMs);
        });
      },
      (err) => {
        console.warn('Soundboard sales listener error:', err);
      },
    );

    // ── Submitted-for-review listener (event: submittedForReview) ───────
    const seenSubmittedIds = new Set<string>();
    const submittedQuery = query(
      collection(db, 'jewelryProductEntries'),
      where('submittedAt', '>', mountTs),
      orderBy('submittedAt', 'desc'),
      limit(20),
    );
    const unsubSubmitted = onSnapshot(
      submittedQuery,
      (snap) => {
        const cfg = eventSoundsRef.current.submittedForReview;
        if (!cfg?.enabled || resolveEventSoundIds(cfg).length === 0) return;
        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const id = change.doc.id;
          if (seenSubmittedIds.has(id)) return;
          seenSubmittedIds.add(id);
          const data = change.doc.data() as { submittedAt?: Timestamp; submittedBy?: string };
          // Individual-only: only the user who submitted the entry hears it.
          if (!myUid || data.submittedBy !== myUid) return;
          const ms = data.submittedAt?.toMillis?.() ?? Date.now();
          if (Date.now() - ms > 30_000) return;
          const sound = pickEventSound(cfg);
          if (!sound) return;
          schedulePlay(sound, ms);
        });
      },
      (err) => console.warn('Soundboard submitted-for-review listener error:', err),
    );

    // ── Shopify push listener (event: pushedToShopify) ──────────────────
    const seenShopifyIds = new Set<string>();
    const shopifyQuery = query(
      collection(db, 'jewelryProductEntries'),
      where('pushedToShopifyAt', '>', mountTs),
      orderBy('pushedToShopifyAt', 'desc'),
      limit(20),
    );
    const unsubShopify = onSnapshot(
      shopifyQuery,
      (snap) => {
        const cfg = eventSoundsRef.current.pushedToShopify;
        if (!cfg?.enabled || resolveEventSoundIds(cfg).length === 0) return;
        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const id = change.doc.id;
          if (seenShopifyIds.has(id)) return;
          seenShopifyIds.add(id);
          const data = change.doc.data() as { pushedToShopifyAt?: Timestamp; pushedToShopifyByUid?: string };
          // Individual-only: only the user who published to CCO (Shopify) hears it.
          if (!myUid || data.pushedToShopifyByUid !== myUid) return;
          const ms = data.pushedToShopifyAt?.toMillis?.() ?? Date.now();
          if (Date.now() - ms > 30_000) return;
          const sound = pickEventSound(cfg);
          if (!sound) return;
          schedulePlay(sound, ms);
        });
      },
      (err) => console.warn('Soundboard shopify listener error:', err),
    );

    // ── eBay push listener (event: pushedToEbay) ────────────────────────
    const seenEbayIds = new Set<string>();
    const ebayQuery = query(
      collection(db, 'jewelryProductEntries'),
      where('ebayPushedAt', '>', mountTs),
      orderBy('ebayPushedAt', 'desc'),
      limit(20),
    );
    const unsubEbay = onSnapshot(
      ebayQuery,
      (snap) => {
        const cfg = eventSoundsRef.current.pushedToEbay;
        if (!cfg?.enabled || resolveEventSoundIds(cfg).length === 0) return;
        snap.docChanges().forEach((change) => {
          if (change.type !== 'added') return;
          const id = change.doc.id;
          if (seenEbayIds.has(id)) return;
          seenEbayIds.add(id);
          const data = change.doc.data() as { ebayPushedAt?: Timestamp; ebayPushedByUid?: string };
          // Individual-only: only the user who published to eBay hears it.
          if (!myUid || data.ebayPushedByUid !== myUid) return;
          const ms = data.ebayPushedAt?.toMillis?.() ?? Date.now();
          if (Date.now() - ms > 30_000) return;
          const sound = pickEventSound(cfg);
          if (!sound) return;
          schedulePlay(sound, ms);
        });
      },
      (err) => console.warn('Soundboard ebay listener error:', err),
    );

    // ── Live-chat listener (event: newChat) ─────────────────────────────
    // Watches conversations whose latest CUSTOMER message lands after mount.
    // Tracks each conversation's last customer-message time so follow-up
    // messages (which arrive as `modified` changes) also fire, while staff
    // replies / typing updates (which don't advance that field) stay silent.
    //
    // Playback differs by message position in the conversation:
    //   • First (initial) customer message of a brand-new conversation →
    //     the configured "Customer chatting with us" event sound.
    //   • Every follow-up customer message → the original subtle chime
    //     (880Hz → 440Hz sweep) that was used before this event existed.
    const chatLastSeen = new Map<string, number>();
    // Conversations we've already greeted with the initial event sound, so
    // every subsequent customer message falls through to the chime.
    const chatInitialPlayed = new Set<string>();
    const chatQuery = query(
      collection(db, 'chat_conversations'),
      where('lastCustomerMessageAt', '>', mountTs),
      orderBy('lastCustomerMessageAt', 'desc'),
      limit(20),
    );
    const unsubChat = onSnapshot(
      chatQuery,
      (snap) => {
        const cfg = eventSoundsRef.current.newChat;
        snap.docChanges().forEach((change) => {
          if (change.type === 'removed') return;
          const id = change.doc.id;
          const data = change.doc.data() as {
            lastCustomerMessageAt?: Timestamp;
            createdAt?: Timestamp;
          };
          const ms = data.lastCustomerMessageAt?.toMillis?.() ?? 0;
          const prev = chatLastSeen.get(id) ?? 0;
          if (ms <= prev) return;
          chatLastSeen.set(id, ms);
          if (!cfg?.enabled) return;
          if (Date.now() - ms > 30_000) return;

          // A conversation is on its "initial" message when its latest
          // customer message is essentially its creation message — i.e. the
          // last-customer-message time is within a short window of when the
          // conversation doc was created. Follow-ups arrive much later.
          const createdMs = data.createdAt?.toMillis?.() ?? 0;
          const isInitial =
            !chatInitialPlayed.has(id) &&
            createdMs > 0 &&
            ms - createdMs < CHAT_INITIAL_WINDOW_MS;

          if (isInitial) {
            chatInitialPlayed.add(id);
            // Initial message → configured event sound (synced across staff).
            if (resolveEventSoundIds(cfg).length === 0) return;
            const sound = pickEventSound(cfg);
            if (!sound) return;
            schedulePlay(sound, ms);
          } else {
            // Follow-up message → the original subtle chime. Honors the same
            // per-device Do-Not-Disturb / mute / volume prefs as event sounds.
            chatInitialPlayed.add(id);
            if (getStoredDnd() || getStoredMuted()) return;
            playLiveChatChime(getStoredVolume());
          }
        });
      },
      (err) => console.warn('Soundboard chat listener error:', err),
    );

    // ── Survey-response listener (event: newReview) ─────────────────────
    // Subscribes to every survey's responses subcollection and plays when a
    // fresh submission carries a 5-star rating. Only fires for users whose
    // role can read survey responses (Master Admin).
    const reviewResponseUnsubs: Array<() => void> = [];
    const seenReviewIds = new Set<string>();
    const unsubSurveys = onSnapshot(
      collection(db, 'customer_surveys'),
      (surveysSnap) => {
        reviewResponseUnsubs.forEach((u) => u());
        reviewResponseUnsubs.length = 0;
        surveysSnap.docs.forEach((surveyDoc) => {
          const rq = query(
            collection(db, 'customer_surveys', surveyDoc.id, 'responses'),
            where('submittedAt', '>', mountTs),
          );
          const u = onSnapshot(
            rq,
            (respSnap) => {
              const cfg = eventSoundsRef.current.newReview;
              if (!cfg?.enabled || resolveEventSoundIds(cfg).length === 0) return;
              respSnap.docChanges().forEach((change) => {
                if (change.type !== 'added') return;
                const id = change.doc.id;
                if (seenReviewIds.has(id)) return;
                seenReviewIds.add(id);
                const data = change.doc.data() as {
                  submittedAt?: Timestamp;
                  answers?: Record<string, unknown>;
                };
                const ms = data.submittedAt?.toMillis?.() ?? Date.now();
                if (Date.now() - ms > 30_000) return;
                // Celebrate top marks only: any rating answer equal to 5.
                const answers = data.answers || {};
                const isFiveStar = Object.values(answers).some((v) => Number(v) === 5);
                if (!isFiveStar) return;
                const sound = pickEventSound(cfg);
                if (!sound) return;
                schedulePlay(sound, ms);
              });
            },
            (err) => console.warn('Soundboard review listener error:', err),
          );
          reviewResponseUnsubs.push(u);
        });
      },
      (err) => console.warn('Soundboard surveys listener error:', err),
    );

    return () => {
      unsubSounds();
      unsubTrigger();
      unsubStop();
      unsubEventMap();
      unsubSales();
      unsubSubmitted();
      unsubShopify();
      unsubEbay();
      unsubChat();
      unsubSurveys();
      reviewResponseUnsubs.forEach((u) => u());
    };
  }, [hasAccess, currentUser?.uid]);

  return null;
};

/**
 * Schedules a sound to play at `referenceServerTimeMs + PLAY_LEAD_MS`. Each
 * recipient computes the same target from the same server timestamp, then
 * uses its own `Date.now()` for the local `setTimeout`. As long as client
 * clocks are roughly in sync (typical office NTP drift is <100ms), every
 * client fires `audio.play()` at the same wall-clock instant.
 *
 * Honors per-device mute and Do-Not-Disturb. No UI / toast — purely audio.
 * Degrades silently when the browser blocks autoplay before the user has
 * interacted with the page.
 */
function schedulePlay(sound: SoundboardSound, referenceServerTimeMs: number): void {
  if (getStoredDnd()) return;
  if (getStoredMuted()) return;

  let audio: HTMLAudioElement;
  try {
    audio = new Audio(sound.downloadUrl);
    audio.volume = getStoredVolume();
    audio.crossOrigin = 'anonymous';
    // Force the browser to start fetching/decoding immediately so the
    // scheduled play() call has a buffered file ready.
    audio.preload = 'auto';
    audio.load();
    registerActiveAudio(audio);
  } catch (err) {
    console.warn('Soundboard playback init error:', err);
    return;
  }

  const targetMs = referenceServerTimeMs + PLAY_LEAD_MS;
  const delay = Math.max(0, targetMs - Date.now());

  const fire = () => {
    audio.play().catch((err) => {
      console.warn('Soundboard playback blocked or failed:', err);
    });
  };

  if (delay === 0) fire();
  else setTimeout(fire, delay);
}

export default SoundboardListener;
